import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    ActionableContainerSkeleton,
    Description,
    FieldGroup,
    Group,
    Label,
    Panel,
    PanelBody,
} from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../../state';
import {
    getDefaultSiteSpaceInSiteStructure,
    getSiteFromOrgAllContent,
    getSiteSpaceInSiteStructure,
} from '../../Organization';
import { EditSpaceLanguageButton, SpaceLink } from '../../Space';
import type { SitesCustomizationPanelProps } from '../types';

/**
 * Settings to configure the language of the content.
 */
export function LanguageSettings(props: SitesCustomizationPanelProps): React.ReactElement {
    return (
        <Panel aria-label="Internationalization">
            <PanelBody>
                <React.Suspense fallback={<ActionableContainerSkeleton />}>
                    <LanguageSettingsFetcher {...props} />
                </React.Suspense>
            </PanelBody>
        </Panel>
    );
}

/**
 * Settings to configure the language.
 */
function LanguageSettingsFetcher(props: SitesCustomizationPanelProps) {
    const { organizationId, site, siteSpaceId, settings } = props;

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId,
        })
    );
    const siteSpace = React.useMemo(() => {
        const siteStructure = getSiteFromOrgAllContent(organizationContent, site.id);
        if (!siteStructure) {
            return null;
        }
        return siteSpaceId
            ? getSiteSpaceInSiteStructure(siteStructure.item.structure, siteSpaceId)
            : getDefaultSiteSpaceInSiteStructure(siteStructure.item.structure);
    }, [organizationContent, siteSpaceId, site.id]);

    if (!siteSpace) {
        return null;
    }

    const defaultLanguage = getTranslationLanguageFromCustomizationLocale(
        settings.internationalization.locale
    );

    return (
        <FieldGroup orientation="horizontal">
            <Group>
                <Label>Localize user interface</Label>
                <Description>
                    Configure the language of <SpaceLink spaceId={siteSpace.space.id} />. The site's
                    user interface will be translated accordingly.
                </Description>
            </Group>
            <EditSpaceLanguageButton space={siteSpace.space} defaultLanguage={defaultLanguage} />
        </FieldGroup>
    );
}

function getTranslationLanguageFromCustomizationLocale(
    locale: api.CustomizationLocale
): api.TranslationLanguage {
    switch (locale) {
        case api.CustomizationLocale.En:
            return api.TranslationLanguage.En;
        case api.CustomizationLocale.Fr:
            return api.TranslationLanguage.Fr;
        case api.CustomizationLocale.Es:
            return api.TranslationLanguage.Es;
        case api.CustomizationLocale.De:
            return api.TranslationLanguage.De;
        case api.CustomizationLocale.Nl:
            return api.TranslationLanguage.Nl;
        case api.CustomizationLocale.No:
            return api.TranslationLanguage.No;
        case api.CustomizationLocale.PtBr:
            return api.TranslationLanguage.PtBr;
        case api.CustomizationLocale.Zh:
            return api.TranslationLanguage.Zh;
        case api.CustomizationLocale.Ja:
            return api.TranslationLanguage.Ja;
        case api.CustomizationLocale.Ru:
            return api.TranslationLanguage.Ru;
        default:
            assertNever(locale);
    }
}
